<template>
  <div
    :class="getLoading && 'bg-white'"
    class="flex-auto px-4 lg:px-10 py-10 pt-0"
  >
    <div
      v-if="getLoading"
      class="bg-white w-full flex flex-col py-10 justify-center items-center"
    >
      <!-- https://gannon.tv/intro -->
      <img width="260" src="@/assets/imgs/loading.gif" alt="loading" />
      <h3>Loading...</h3>
    </div>
    <div v-if="!hasContent && !getLoading">
      <p class="justify-center items-center flex mt-10">
        This component has no attributes
      </p>
    </div>
    <form v-if="hasContent">
      <h6
        v-if="
          getBasicAttributes ? Object.keys(getBasicAttributes).length : null
        "
        class="text-blueGray-400 text-sm mt-3 ml-4 mb-6 font-bold uppercase"
      >
        Instance Info
      </h6>
      <div class="flex flex-wrap">
        <div v-if="!getLoading" class="w-full lg:w-4/12 px-4">
          <label
            class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
          >
            Instance Name
          </label>
          <input
            type="text"
            v-model="formDataLocal.instance_name"
            class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
          />
        </div>
      </div>
      <hr v-if="!getLoading" class="mt-6 border-b-1 border-blueGray-300" />
      <h6
        v-if="
          getBasicAttributes ? Object.keys(getBasicAttributes).length : null
        "
        class="text-blueGray-400 text-sm mt-3 ml-4 mb-6 font-bold uppercase"
      >
        Basic
      </h6>
      <div class="flex flex-wrap">
        <div
          class="w-full lg:w-4/12 px-4"
          v-for="(value, name) in basicAttributes"
          :key="name"
        >
          <div v-if="value.type === 'number'" class="relative w-full mb-3">
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                :title="formatAttrName(name)"
                :description="value.description"
              />
            </label>
            <input
              type="number"
              autocomplete="off"
              data-lpignore="true"
              v-model="formDataLocal['attributes'][name]"
              class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
            />
          </div>
          <div
            v-else-if="value.type === 'short_text'"
            class="relative w-full mb-3"
          >
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                :title="formatAttrName(name)"
                :description="value.description"
              />
            </label>
            <input
              type="text"
              v-model="formDataLocal['attributes'][name]"
              class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
            />
          </div>
          <div
            v-else-if="value.type === 'select' || value.type === 'boolean'"
            class="relative w-full mb-3"
          >
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                :title="formatAttrName(name)"
                :description="value.description"
              />
            </label>
            <select
              v-model="formDataLocal['attributes'][name]"
              class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
            >
              <option
                v-for="(option, index) in changeOptionsFormat(value.options)"
                :key="index"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <div v-else-if="value.type === 'url'" class="relative w-full mb-3">
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                :title="formatAttrName(name)"
                :description="value.description"
              />
            </label>
            <URLInput
              :url="
                formData['attributes'][name] ? formData['attributes'][name] : ''
              "
              @update:url="(val) => handleUpdateValue(val, name)"
            />
          </div>
          <div v-else-if="value.type === 'icon'" class="relative w-full mb-3">
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                :title="formatAttrName(name)"
                :description="value.description"
              />
            </label>
            <IconPicker
              :icons="value.options"
              :icon="
                formData['attributes'][name] ? formData['attributes'][name] : ''
              "
              @update:icon="(val) => handleUpdateValue(val, name)"
            />
          </div>
          <div v-else class="relative w-full mb-3">
            <label
              class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
            >
              <AttrTitleWithDescription
                :title="formatAttrName(name)"
                :description="value.description"
              />
            </label>
            <input
              v-model="formDataLocal['attributes'][name]"
              type="text"
              class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
            />
          </div>
        </div>
      </div>
      <hr v-if="!getLoading" class="mt-6 border-b-1 border-blueGray-300" />
      <h6
        v-if="
          getBasicAttributes ? Object.keys(getBasicAttributes).length : null
        "
        class="text-blueGray-400 text-sm mt-3 ml-4 mb-6 font-bold uppercase"
      >
        Elements
      </h6>
      <div class="flex flex-wrap">
        <DNElement
          v-for="index in itemNumber || 1"
          :key="index"
          :index="String(index)"
          :elementName="elementName"
          :componentId="componentId"
          :formData="formData['attributes']"
          :dynamicAttributes="dynamicAttributes"
          @update:formData="handleUpdateFormData"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as Actions from "@/store/actions.type";
import DNElement from "@/components/ComponentModule/DynamicNumComponent/DNElement";
import AttrTitleWithDescription from "@/components/Shared/Headers/AttrTitleWithDescription";
import URLInput from "@/components/Shared/Input/URLInput";
import IconPicker from "@/components/Shared/Input/IconPicker";

export default {
  name: "DNComponentCreateForm",
  components: {
    DNElement,
    URLInput,
    IconPicker,
    AttrTitleWithDescription
  },
  data() {
    return {
      lastItemNum: 0
    };
  },
  props: {
    elementName: {
      type: String,
      required: true
    },
    componentId: {
      type: String,
      required: true
    },
    formData: {
      type: Object,
      required: true
    },
    attributesCategory: {
      type: Object,
      required: true
    }
  },
  mounted() {
    this.$store.dispatch(Actions.CLEAR_COMPONENT_ATTRIBUTES);
  },
  watch: {
    getBasicAttributes() {
      if (this.getBasicAttributes) {
        Object.keys(this.getBasicAttributes).forEach((item) => {
          if (!this.formDataLocal["attributes"][item]) {
            this.formDataLocal["attributes"][item] =
              this.getBasicAttributes[item].value;
          }
        });
      }
    },
    getAdditionalAttributes() {
      if (this.getAdditionalAttributes) {
        Object.keys(this.getAdditionalAttributes).forEach((item) => {
          if (!this.formDataLocal["attributes"][item]) {
            this.formDataLocal["attributes"][item] =
              this.getAdditionalAttributes[item].value;
          }
        });
      }
    },
    getAllAttributes() {
      this.itemNumber = 1;
    },
    dynamicAttributes() {
      if (Object.keys(this.dynamicAttributes).length) {
        // set default value for formdata
        Object.keys(this.dynamicAttributes).forEach((item) => {
          if (!this.formDataLocal.attributes[`${item}1`]) {
            this.formDataLocal.attributes[`${item}1`] = "";
          }
        });
      }
    },
    basicAttributes() {
      if (Object.keys(this.basicAttributes).length) {
        // set default value for formdata
        Object.keys(this.basicAttributes).forEach((item) => {
          if (!this.formDataLocal.attributes[`${item}`]) {
            this.formDataLocal.attributes[`${item}`] = "";
          }
        });
      }
    },
    itemNumber(newValue, oldValue) {
      if (this.itemNumber > 0) {
        // record the previous value
        if (oldValue) {
          this.lastItemNum = oldValue;
        }
        for (let i = 0; i < this.itemNumber; i++) {
          Object.keys(this.dynamicAttributes).forEach((item) => {
            if (!this.formDataLocal.attributes[`${item}${i + 1}`]) {
              this.formDataLocal.attributes[`${item}${i + 1}`] = "";
            }
          });
        }
        if (this.itemNumber < this.lastItemNum) {
          Object.keys(this.dynamicAttributes).forEach((item) => {
            for (let i = this.itemNumber + 1; i <= this.lastItemNum; i++) {
              delete this.formDataLocal.attributes[`${item}${i}`];
            }
          });
        }
      }
      // set the max item number to 20
      if (this.itemNumber > 20) {
        this.itemNumber = 20;
      }

      // set the min item number to 1
      if (this.itemNumber <= 0) {
        this.itemNumber = 1;
      }
    }
  },
  computed: {
    itemNumber: {
      get: function () {
        const itemNumAttrName = this.attributesCategory.basic_attributes.find(
          (item) => item.includes("cols")
        );
        return parseInt(this.formDataLocal.attributes[itemNumAttrName]);
      },
      set: function (val) {
        const itemNumAttrName = this.attributesCategory.basic_attributes.find(
          (item) => item.includes("cols")
        );
        this.formDataLocal.attributes[itemNumAttrName] = parseInt(val);
      }
    },
    formDataLocal: {
      get: function () {
        return this.formData;
      },
      set: function (val) {
        this.$emit("update:formData", val);
      }
    },
    basicAttributes() {
      let result = {};
      if (this.getAllAttributes) {
        const attrNames = Object.keys(this.getAllAttributes).filter((item) => {
          return this.attributesCategory.basic_attributes.includes(item);
        });
        attrNames.forEach((item) => {
          result[item] = this.getAllAttributes[item];
        });
        return result;
      } else {
        return {};
      }
    },
    dynamicAttributes() {
      let result = {};
      if (this.getAllAttributes) {
        const attrNames = Object.keys(this.getAllAttributes).filter((item) => {
          const names = this.attributesCategory.dynamic_attributes.map(
            (item) => {
              return `${item}1`;
            }
          );
          return names.includes(item);
        });
        attrNames.forEach((item) => {
          result[item.substring(0, item.length - 1)] =
            this.getAllAttributes[item];
        });
        return result;
      } else {
        return {};
      }
    },
    hasContent() {
      if (
        (this.getBasicAttributes &&
          this.getAdditionalAttributes &&
          Object.keys(this.getBasicAttributes).length +
            Object.keys(this.getAdditionalAttributes).length ===
            0) ||
        !this.getBasicAttributes ||
        !this.getAdditionalAttributes
      ) {
        return false;
      } else {
        return true;
      }
    },
    ...mapGetters([
      "getLoading",
      "getBasicAttributes",
      "getAdditionalAttributes",
      "getAllAttributes"
    ])
  },
  methods: {
    formatAttrName(name) {
      const nameArray = name.split("_");
      nameArray.splice(0, 2);
      return nameArray.join(" ");
    },
    changeOptionsFormat(options) {
      const newOptions = options.map((item) => {
        return { label: item, value: item };
      });
      return newOptions;
    },
    handleUpdateFormData(val) {
      this.formDataLocal["attribute"] = val;
    },
    handleUpdateValue(val, name) {
      this.formDataLocal[name] = val;
    }
  }
};
</script>
